import { useMemo } from 'react';

import { EmergencyFundAdvancedPlanTeaser, EmergencyFundStoredSection } from '../../components/emergencyFund/EmergencyFundStoredSection';
import { EmergencyFundCapitalDistribution } from '../../components/emergencyFund/EmergencyFundCapitalDistribution';
import { EmergencyFundEmptyState } from '../../components/emergencyFund/EmergencyFundEmptyState';
import { EmergencyFundPeaceCard } from '../../components/emergencyFund/EmergencyFundPeaceCard';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import {
  computeYearProgressEur,
  estimateMonthsFromAllocation,
  parseAmount,
} from '../../lib/emergencyFundDisplayUtils';
import {
  useAllocationSetupQuery,
  useBuildGoalSuggestionQuery,
  useEmergencyFundConfigQuery,
  useFundHealthQuery,
} from '../../features/emergencyFund/queries';
import { useSnapshotsListQuery } from '../../features/snapshots/queries';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import pt from '../../locales/pt';

const INVEST_CATS = new Set(['brokerage', 'crypto', 'precious_metal']);

function EmergencyFundContentFallback() {
  return (
    <div className="flex flex-col gap-space-3">
      <SectionSkeleton variant="hero" />
      <SectionSkeleton variant="card-grid" className="sm:grid-cols-1" />
    </div>
  );
}

export function EmergencyFundDashboard() {
  const {
    data: snapshots,
    isPending: snapshotsPending,
    isFetching: snapshotsFetching,
  } = useSnapshotsListQuery({ is_completed: true });
  const {
    data: config,
    isPending: configPending,
    isFetching: configFetching,
  } = useEmergencyFundConfigQuery();
  const {
    data: health,
    isPending: healthPending,
    isFetching: healthFetching,
  } = useFundHealthQuery();
  const {
    data: setup,
    isPending: setupPending,
    isFetching: setupFetching,
  } = useAllocationSetupQuery();

  const configShell = useQueryShellState({
    data: config,
    isPending: configPending,
    isFetching: configFetching,
  });
  const healthShell = useQueryShellState({
    data: health,
    isPending: healthPending,
    isFetching: healthFetching,
  });
  const setupShell = useQueryShellState({
    data: setup,
    isPending: setupPending,
    isFetching: setupFetching,
  });
  const snapshotsShell = useQueryShellState({
    data: snapshots,
    isPending: snapshotsPending,
    isFetching: snapshotsFetching,
  });

  const completedSnapshots = useMemo(
    () => (Array.isArray(snapshots) ? snapshots.filter((s) => s.is_completed !== false) : []),
    [snapshots],
  );
  const hasSelfie = completedSnapshots.length > 0;
  const isConfigured =
    config?.wizard_step === 'completed' ||
    Number(setup?.ef_allocated_total ?? 0) + Number(setup?.base_allocated_total ?? 0) > 0;

  const { data: suggestion } = useBuildGoalSuggestionQuery({ enabled: isConfigured });

  // Snapshots only needed to decide empty-state CTA when not configured.
  const contentReady =
    !configShell.showBlockingSkeleton &&
    !healthShell.showBlockingSkeleton &&
    !setupShell.showBlockingSkeleton &&
    (isConfigured || !snapshotsShell.showBlockingSkeleton);

  const storedAccounts = useMemo(() => {
    if (!setup?.accounts?.length) return [];
    return setup.accounts
      .filter((row) => {
        const base = parseAmount(row.base_amount_eur);
        const ef = parseAmount(row.ef_amount_eur);
        return base > 0 || ef > 0;
      })
      .map((row) => ({
        id: row.account_id,
        accountId: row.account_id,
        name: row.account_name,
        category: row.category,
        amountEur: parseAmount(row.base_amount_eur) + parseAmount(row.ef_amount_eur),
      }));
  }, [setup]);

  const storedTotal = useMemo(
    () => storedAccounts.reduce((acc, row) => acc + row.amountEur, 0),
    [storedAccounts],
  );

  const totalAccountBalance = useMemo(
    () =>
      Array.isArray(setup?.accounts)
        ? setup.accounts.reduce((sum, row) => sum + parseAmount(row.balance_eur), 0)
        : 0,
    [setup],
  );
  const forInvestment = useMemo(
    () =>
      Array.isArray(setup?.accounts)
        ? setup.accounts
            .filter((row) => INVEST_CATS.has(row.category))
            .reduce((sum, row) => sum + parseAmount(row.balance_eur), 0)
        : 0,
    [setup],
  );
  const unallocatedSavings = Math.max(0, totalAccountBalance - storedTotal - forInvestment);

  const targetMonths = Number(config?.ef_months_of_coverage ?? health?.target_months ?? 3);
  const targetEur =
    parseAmount(config?.ef_custom_target) || parseAmount(health?.emergency_fund_target);

  // Primary: always based on the wizard target (storedTotal / target × months).
  // This reflects what the user configured, independent of snapshot expenses.
  const monthsCovered = useMemo(
    () => estimateMonthsFromAllocation(storedTotal, targetEur, targetMonths),
    [storedTotal, targetEur, targetMonths],
  );

  // Secondary: months covered at the actual spending rate (health endpoint).
  // Only shown when it meaningfully differs from the primary figure.
  const healthMonthsCovered = useMemo(() => {
    if (Number(health?.snapshots_used ?? 0) < 3) return null;
    const raw = Number(health?.months_covered ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    if (raw > targetMonths * 3) return null;
    if (Math.abs(raw - monthsCovered) < 0.5) return null;
    return raw;
  }, [health, targetMonths, monthsCovered]);

  const yearProgress = useMemo(
    () => computeYearProgressEur(snapshots, storedAccounts.map((row) => row.accountId)),
    [snapshots, storedAccounts],
  );

  const hasStoredAccounts = storedAccounts.length > 0;
  const hasCapitalBreakdown = totalAccountBalance > 0;

  return (
    <div className="flex flex-col gap-[11px] pb-6 md:gap-4">
      <header className="hidden md:flex flex-col gap-1">
        <h1 className="type-title-page text-foreground">{pt.emergencyFund.title}</h1>
        {isConfigured ? (
          <p className="type-body-sm text-muted-foreground">{pt.emergencyFund.subtitle}</p>
        ) : null}
      </header>

      <PageContentShell ready={contentReady} fallback={<EmergencyFundContentFallback />}>
        {!isConfigured ? (
          <EmergencyFundEmptyState hasSelfie={hasSelfie} />
        ) : (
          <div className="grid grid-cols-1 gap-space-5 md:grid-cols-2">
            <div className="md:col-start-1 md:row-start-1">
              <EmergencyFundPeaceCard
                health={health}
                storedTotal={storedTotal}
                targetEur={targetEur}
                targetMonths={targetMonths}
                monthsCovered={monthsCovered}
                healthMonthsCovered={healthMonthsCovered}
                suggestedMonthly={suggestion?.suggested_monthly_contribution}
                monthsHorizon={suggestion?.months_horizon || targetMonths}
                yearProgress={yearProgress}
              />
            </div>

            {hasStoredAccounts ? (
              <div className="md:col-start-2 md:row-start-1">
                <EmergencyFundStoredSection accounts={storedAccounts} totalSaved={storedTotal} />
              </div>
            ) : null}

            {hasCapitalBreakdown ? (
              <div className="md:col-start-2 md:row-start-2">
                <EmergencyFundCapitalDistribution
                  efTotal={storedTotal}
                  investedTotal={forInvestment}
                  idleTotal={unallocatedSavings}
                  avgMonthlyExpenses={health?.avg_monthly_expenses}
                  snapshotsUsed={health?.snapshots_used}
                />
              </div>
            ) : null}

            <div className="md:col-start-1 md:row-start-2">
              <EmergencyFundAdvancedPlanTeaser />
            </div>
          </div>
        )}
      </PageContentShell>
    </div>
  );
}

export default EmergencyFundDashboard;
